/**
 * MODULE: domain/pages/frontend/utils/databaseFieldIcons.tsx
 * PURPOSE: Notion-style field type icons for database column headers
 * RECENT: 2026-05-24 [2.2.1186] — Aa and react-icons/hi glyphs for database table headers
 * AGENT_SAFETY: 🟢 SAFE
 * FILE_SIZE: 51/150
 */

import type { ReactNode } from 'react';
import {
  HiAtSymbol,
  HiCalendar,
  HiCheck,
  HiClock,
  HiHashtag,
  HiLink,
  HiPhone,
  HiUser,
} from 'react-icons/hi';
import type { FieldDefinition, FieldType } from '../../contracts/pages.types';

function AaIcon({ className }: { className?: string }) {
  return (
    <span className={['font-serif text-[11px] font-semibold leading-none', className].filter(Boolean).join(' ')}>
      Aa
    </span>
  );
}

const FIELD_TYPE_ICONS: Record<FieldType, (props: { className?: string }) => ReactNode> = {
  text: AaIcon,
  number: ({ className }) => <HiHashtag className={className} aria-hidden />,
  select: AaIcon,
  multi_select: AaIcon,
  date: ({ className }) => <HiCalendar className={className} aria-hidden />,
  checkbox: ({ className }) => <HiCheck className={className} aria-hidden />,
  url: ({ className }) => <HiLink className={className} aria-hidden />,
  email: ({ className }) => <HiAtSymbol className={className} aria-hidden />,
  phone: ({ className }) => <HiPhone className={className} aria-hidden />,
  created_at: ({ className }) => <HiClock className={className} aria-hidden />,
  created_by: ({ className }) => <HiUser className={className} aria-hidden />,
};

export function DatabaseFieldIcon({
  field,
  className = 'h-3.5 w-3.5 shrink-0 text-gray-400 dark:text-gray-500',
}: {
  field: FieldDefinition;
  className?: string;
}) {
  const Icon = FIELD_TYPE_ICONS[field.field_type] ?? AaIcon;
  return <Icon className={className} />;
}
